import type { PublicAPI, PTPlugin, EventCallback, SDKModule, DebugInfo, PreflightReport } from '../types';
import type { SDK } from '../core/sdk';
import { trackPtengine } from '../utils/track';
import { getDevice } from '../utils/session';
import { findFormWrapperByName } from '../utils/dom';
import { dumpLogs, log } from '../utils/logger';
import { runPreflight } from './preflight';
import { VERSION } from '../config';

/**
 * Create the public API exposed as window.PT.
 *
 * Surface:
 *   PT.version              — build sha / 'dev'
 *   PT.preview              — true inside editor preview
 *   PT.track(name, props)   — send custom event (preview: dispatch only)
 *   PT.submitForm(nameOrId) — trigger a form's submit pipeline
 *   PT.getPageInfo()        — { pageId, profileId, sessionId, device, utm, preview }
 *   PT.getDebugInfo()       — version + recent log ring buffer
 *   PT.getPreflightReport() — synchronous DOM contract checker
 *   PT.on/off               — SDK event bus
 *   PT.use(plugin)          — install once-per-name plugin
 *   PT.registerModule(mod)  — install a runtime SDKModule (init() called immediately)
 */
export function createPublicAPI(sdk: SDK): PublicAPI {
  const installedPlugins = new Set<string>();

  const api: PublicAPI = {
    get version() {
      return VERSION;
    },

    get preview() {
      return sdk.preview;
    },

    track(eventName: string, properties?: Record<string, unknown>): void {
      if (sdk.preview) {
        log('info', 'preview: PT.track skipped ' + eventName, properties);
        try {
          document.dispatchEvent(new CustomEvent('pt-preview-action', {
            detail: { kind: 'track', reason: eventName, payload: properties },
          }));
        } catch (e) {
          log('warn', 'PT.track preview dispatch failed', e);
        }
        return;
      }
      trackPtengine(eventName, properties);
    },

    submitForm(formNameOrId: string, data?: Record<string, string>): void {
      const wrapper = findFormWrapperByName(formNameOrId);
      if (!wrapper) {
        log('warn', 'PT.submitForm: no wrapper named "' + formNameOrId + '"');
        return;
      }
      const form = wrapper.querySelector('form');
      if (!form) {
        log('warn', 'PT.submitForm: no <form> in wrapper "' + formNameOrId + '"');
        return;
      }

      if (data) {
        try {
          form.dispatchEvent(new CustomEvent('pt-form-submit', { detail: data, bubbles: true }));
        } catch (e) {
          log('error', 'PT.submitForm dispatch failed', e);
        }
        return;
      }

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    },

    getPageInfo() {
      return {
        pageId: sdk.config.pageId,
        profileId: sdk.config.profileId || '',
        sessionId: sdk.sessionId,
        device: getDevice(),
        utm: sdk.utm,
        preview: sdk.preview,
      };
    },

    getDebugInfo(): DebugInfo {
      return {
        version: VERSION,
        preview: sdk.preview,
        logs: dumpLogs(),
      };
    },

    getPreflightReport(): PreflightReport {
      return runPreflight();
    },

    on(event: string, callback: EventCallback): void {
      sdk.on(event, callback);
    },

    off(event: string, callback: EventCallback): void {
      sdk.off(event, callback);
    },

    use(plugin: PTPlugin): void {
      if (!plugin || !plugin.name || typeof plugin.install !== 'function') {
        log('warn', 'PT.use: invalid plugin', plugin);
        return;
      }
      if (installedPlugins.has(plugin.name)) {
        log('warn', 'PT.use: plugin "' + plugin.name + '" already installed; skipping');
        return;
      }
      installedPlugins.add(plugin.name);
      try {
        plugin.install(api);
      } catch (e) {
        log('error', 'plugin install threw: ' + plugin.name, e);
      }
    },

    registerModule(module: SDKModule): void {
      if (!module || !module.name || typeof module.init !== 'function') {
        log('warn', 'PT.registerModule: invalid module', module);
        return;
      }
      sdk.use(module);
    },
  };

  return api;
}
